import React, { useEffect, useMemo, useRef, useState } from "react";
import { db } from "../firebase";
import { doc, getDoc } from "firebase/firestore";
import { ArrowLeft2, ArrowRight2, Eye, EyeSlash, Message, SearchNormal1, Trash, VideoPlay, Warning2 } from "iconsax-reactjs";
import {
  subscribeReels,
  setReelStatus,
  deleteReel,
  formatCount,
  ReelPropertyUnavailableError,
} from "../utils/reelsShared";
import ReelCard from "./reels/ReelCard";
import ReelVideoModal from "./reels/ReelVideoModal";
import ReelCommentsModal from "./reels/ReelCommentsModal";
import ReelConfirmModal from "./reels/ReelConfirmModal";
import "./ReelsPanel.css";

const STATUS_TABS = [
  { id: "all", label: "All" },
  { id: "active", label: "Live" },
  { id: "inactive", label: "Hidden" },
];

// Reel cards carry a cover image each; paging keeps a large corpus from
// pulling hundreds of thumbnails on one screen.
const ITEMS_PER_PAGE = 12;

export default function ReelsPanel() {
  const [reels, setReels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [activeTab, setActiveTab] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  const [previewReel, setPreviewReel] = useState(null);
  const [commentsReel, setCommentsReel] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  const [busyId, setBusyId] = useState(null);

  // Dealer names and listing titles are looked up once per id ever seen; the
  // ref holds what has been requested so a re-render never re-fetches.
  const [dealers, setDealers] = useState({});
  const [properties, setProperties] = useState({});
  const requestedDealers = useRef(new Set());
  const requestedProperties = useRef(new Set());

  useEffect(() => {
    const unsubscribe = subscribeReels(
      {},
      (list) => { setReels(list); setLoading(false); setLoadError(null); },
      () => { setLoadError("Could not load reels. Reload the page to try again."); setLoading(false); }
    );
    return () => unsubscribe();
  }, []);

  useEffect(() => {
    reels.forEach((reel) => {
      const uid = reel.dealerId;
      if (uid && !requestedDealers.current.has(uid)) {
        requestedDealers.current.add(uid);
        getDoc(doc(db, "users", uid))
          .then((snap) => {
            const data = snap.exists() ? snap.data() : null;
            setDealers((prev) => ({
              ...prev,
              [uid]: data ? (data.name || data.contactNumber || "Unnamed dealer") : "Deleted account",
            }));
          })
          .catch(() => setDealers((prev) => ({ ...prev, [uid]: "Unknown dealer" })));
      }

      const pid = reel.propertyId;
      if (pid && !requestedProperties.current.has(pid)) {
        requestedProperties.current.add(pid);
        getDoc(doc(db, "properties", pid))
          .then((snap) => {
            const data = snap.exists() ? snap.data() : null;
            setProperties((prev) => ({
              ...prev,
              [pid]: !data || data.status === "deleted"
                ? "Listing removed"
                : (data.title || "Untitled listing"),
            }));
          })
          .catch(() => setProperties((prev) => ({ ...prev, [pid]: "" })));
      }
    });
  }, [reels]);

  const summary = useMemo(() => reels.reduce(
    (acc, r) => ({
      total: acc.total + 1,
      live: acc.live + (r.status === "active" ? 1 : 0),
      hidden: acc.hidden + (r.status !== "active" ? 1 : 0),
      views: acc.views + (r.views || 0),
    }),
    { total: 0, live: 0, hidden: 0, views: 0 }
  ), [reels]);

  const filteredReels = useMemo(() => {
    const term = searchQuery.trim().toLowerCase();
    return reels.filter((reel) => {
      if (activeTab === "active" && reel.status !== "active") return false;
      if (activeTab === "inactive" && reel.status === "active") return false;
      if (!term) return true;
      const haystack = [
        reel.caption,
        reel.city,
        reel.locality,
        dealers[reel.dealerId],
        properties[reel.propertyId],
        reel.id,
      ].filter(Boolean).join(" ").toLowerCase();
      return haystack.includes(term);
    });
  }, [reels, activeTab, searchQuery, dealers, properties]);

  // A filter change can leave the viewer on a page that no longer exists.
  const totalPages = Math.max(1, Math.ceil(filteredReels.length / ITEMS_PER_PAGE));
  useEffect(() => { setCurrentPage(1); }, [activeTab, searchQuery]);
  const safePage = Math.min(currentPage, totalPages);
  const pagedReels = filteredReels.slice((safePage - 1) * ITEMS_PER_PAGE, safePage * ITEMS_PER_PAGE);

  const handleToggleStatus = async (reel) => {
    const nextStatus = reel.status === "active" ? "inactive" : "active";
    setBusyId(reel.id);
    try {
      await setReelStatus(reel, nextStatus);
    } catch (err) {
      if (err instanceof ReelPropertyUnavailableError) {
        alert("That listing was deleted, so this reel can no longer be published.");
      } else {
        console.error("Failed to update reel status:", err);
        alert("Could not update the reel status. Please try again.");
      }
    } finally {
      setBusyId(null);
    }
  };

  const handleConfirmDelete = async () => {
    if (!pendingDelete) return;
    setBusyId(pendingDelete.id);
    try {
      await deleteReel(pendingDelete);
      setPendingDelete(null);
    } catch (err) {
      console.error("Failed to delete reel:", err);
      alert("Could not delete the reel. Please try again.");
    } finally {
      setBusyId(null);
    }
  };

  const tabCounts = { all: summary.total, active: summary.live, inactive: summary.hidden };
  const pageStart = (safePage - 1) * ITEMS_PER_PAGE + 1;
  const pageEnd = Math.min(safePage * ITEMS_PER_PAGE, filteredReels.length);

  return (
    <div className="reels-panel">
      <div className="stat-strip">
        <div className="stat">
          <span className="stat-label">Reels</span>
          <span className="stat-value">{formatCount(summary.total)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Live</span>
          <span className="stat-value">{formatCount(summary.live)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Hidden</span>
          <span className="stat-value">{formatCount(summary.hidden)}</span>
        </div>
        <div className="stat">
          <span className="stat-label">Total views</span>
          <span className="stat-value">{formatCount(summary.views)}</span>
        </div>
      </div>

      <div className="toolbar">
        <div className="search-field reels-search">
          <SearchNormal1 size={16} variant="Linear" aria-hidden="true" />
          <input
            type="search"
            className="input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search caption, dealer, listing or city"
            aria-label="Search reels"
          />
        </div>
        <div className="segmented" role="group" aria-label="Filter by status">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              className={activeTab === tab.id ? "active" : ""}
              aria-pressed={activeTab === tab.id}
              onClick={() => setActiveTab(tab.id)}
            >
              {tab.label}
              {!loading && <span className="reel-seg-count">{formatCount(tabCounts[tab.id])}</span>}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <div className="reel-grid" aria-busy="true">
          <span className="sr-only" role="status">Loading reels</span>
          {Array.from({ length: 8 }, (_, i) => (
            <div key={i} className="reel-card reel-card-skeleton" aria-hidden="true">
              <div className="skeleton reel-skel-poster" />
              <div className="reel-card-body">
                <div className="skeleton reel-skel-line is-short" />
                <div className="skeleton reel-skel-line is-wide" />
                <div className="skeleton reel-skel-line is-mid" />
              </div>
            </div>
          ))}
        </div>
      ) : loadError ? (
        <p className="reel-error-banner" role="alert">
          <Warning2 size={18} variant="Linear" aria-hidden="true" />
          {loadError}
        </p>
      ) : filteredReels.length === 0 ? (
        <div className="empty-state reel-empty">
          <span className="empty-state-icon" aria-hidden="true">
            <VideoPlay size={22} variant="Linear" />
          </span>
          <h3>{reels.length === 0 ? "No reels yet" : "No reels match"}</h3>
          <p>
            {reels.length === 0
              ? "Reels that dealers publish from the app or the dealer portal appear here, ready to hide or remove."
              : "Try a different search term or status filter."}
          </p>
          {reels.length > 0 && (
            <button
              type="button"
              className="btn-secondary"
              onClick={() => { setSearchQuery(""); setActiveTab("all"); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <>
          <div className="reel-grid">
            {pagedReels.map((reel) => {
              const busy = busyId === reel.id;
              const isLive = reel.status === "active";
              return (
                <ReelCard
                  key={reel.id}
                  reel={reel}
                  dealerName={dealers[reel.dealerId] || "Loading…"}
                  propertyTitle={properties[reel.propertyId]}
                  onPreview={setPreviewReel}
                  actions={
                    <>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => setCommentsReel(reel)}
                        aria-label={`Comments (${formatCount(reel.commentsCount)})`}
                        title="Comments"
                      >
                        <Message size={18} variant="Linear" />
                      </button>
                      <button
                        type="button"
                        className="btn-icon"
                        onClick={() => handleToggleStatus(reel)}
                        disabled={busy}
                        aria-label={isLive ? "Hide from feed" : "Publish to feed"}
                        title={isLive ? "Hide from feed" : "Publish to feed"}
                      >
                        {isLive ? <EyeSlash size={18} variant="Linear" /> : <Eye size={18} variant="Linear" />}
                      </button>
                      <button
                        type="button"
                        className="btn-icon reel-action-danger"
                        onClick={() => setPendingDelete(reel)}
                        disabled={busy}
                        aria-label="Delete reel"
                        title="Delete reel"
                      >
                        <Trash size={18} variant="Linear" />
                      </button>
                    </>
                  }
                />
              );
            })}
          </div>

          {totalPages > 1 && (
            <nav className="pagination" aria-label="Reel pages">
              <span>
                Showing {pageStart} to {pageEnd} of {filteredReels.length}
              </span>
              <div className="pagination-pages">
                <button
                  type="button"
                  className="page-btn"
                  disabled={safePage === 1}
                  onClick={() => setCurrentPage(safePage - 1)}
                  aria-label="Previous page"
                >
                  <ArrowLeft2 size={16} variant="Linear" />
                </button>
                <span className="reels-page-status" aria-live="polite">
                  Page {safePage} of {totalPages}
                </span>
                <button
                  type="button"
                  className="page-btn"
                  disabled={safePage === totalPages}
                  onClick={() => setCurrentPage(safePage + 1)}
                  aria-label="Next page"
                >
                  <ArrowRight2 size={16} variant="Linear" />
                </button>
              </div>
            </nav>
          )}
        </>
      )}

      {previewReel && <ReelVideoModal reel={previewReel} onClose={() => setPreviewReel(null)} />}
      {commentsReel && <ReelCommentsModal reel={commentsReel} onClose={() => setCommentsReel(null)} />}

      <ReelConfirmModal
        open={!!pendingDelete}
        title="Delete this reel?"
        message="The video, its cover, its comments and all of its stats will be permanently removed. This cannot be undone."
        confirmLabel="Delete reel"
        busy={!!pendingDelete && busyId === pendingDelete.id}
        onConfirm={handleConfirmDelete}
        onCancel={() => setPendingDelete(null)}
      />
    </div>
  );
}
